/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { ScreenReaderTip, Icon, announce } from 'sarsaparilla';
import { GridHeaderCell } from 'ui-permit/src/components/detailed-availability-page-new/Grid';

const propTypes = {
    isAsc: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isSorted: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onSortToggle: PropTypes.func,
    // onSortToggle: (boolean, boolean, string) => void,
};

export default function SortableTableColumnHeader({
    isAsc = false,
    isDisabled = false,
    isSorted = false,
    name,
    onSortToggle,
}) {
    const [isSortedState, setIsSortedState] = useState(isSorted);
    const [isAscState, setIsAscState] = useState(isAsc);

    useEffect(() => {
        if (isSorted !== isSortedState) {
            setIsSortedState(isSorted);
        }
    }, [isSorted, isSortedState, setIsSortedState]);

    useEffect(() => {
        if (isAsc !== isAscState) {
            setIsAscState(isAsc);
        }
    }, [isAsc, isAscState, setIsAscState]);

    const handleSortToggle = useCallback(() => {
        announce(`Sorted by ${name}: ${isAscState ? 'ascending' : 'descending'}`);

        if (isSortedState) {
            setIsAscState((prevIsAsc) => !prevIsAsc);
            onSortToggle(isSortedState, !isAscState, name);
        } else {
            setIsSortedState(true);
            setIsAscState(true);
            onSortToggle(true, true, name);
        }
    }, [name, isAscState, isSortedState, setIsAscState, setIsSortedState, onSortToggle]);

    let sort = 'none';
    if (isSortedState) {
        sort = isAscState ? 'ascending' : 'descending';
    }

    return (
        <GridHeaderCell
            className="sortable-column-header"
            aria-sort={sort}
            role="columnheader"
        >
            <ScreenReaderTip content="Click to sort the table by this column">
                <button type="button" onClick={handleSortToggle} disabled={isDisabled}>
                    <span className="title">{name}</span>
                    <div className="sort">
                        <div
                            className={cx({
                                disabled: isSortedState && isAscState,
                                sorted: isSortedState && !isAscState,
                                enabled: !isSortedState,
                            })}
                        >
                            <Icon iconName="arrow-up" />
                        </div>
                        <div
                            className={cx({
                                disabled: isSortedState && !isAscState,
                                sorted: isSortedState && isAscState,
                                enabled: !isSortedState,
                            })}
                        >
                            <Icon iconName="arrow-down" />
                        </div>
                    </div>
                </button>
            </ScreenReaderTip>
        </GridHeaderCell>
    );
}

SortableTableColumnHeader.propTypes = propTypes;
